import { Injectable } from '@angular/core';
import { Observable, defer, map, of, switchMap, tap } from 'rxjs';
import { SubriseUser, SubriseUserDetails, UserListQuery } from '@beyou/app/core/models/users/subrise-user.model';
import { PagedResponse } from '@beyou/app/core/dto/paged-response';
import { Session, SessionListQuery } from '@beyou/app/core/models/sessions/session.model';
import { UserAffiliation } from '@beyou/app/core/models/users/user-affiliation.model';
import { PracticeContextService } from '@beyou/app/core/services/practice-context.service';
import { SessionService } from '@beyou/app/core/store/features/session.service';
import { UserService } from '@beyou/app/core/store/features/user.service';
import { UserApiService } from '@beyou/app/core/services/api/user-api.service';
import { UserRelationships } from '@beyou/app/core/models/users/user-relationships.model';
import { PatientService } from './patient.service';

/**
 * Where the clients screens get their clients and sessions from, which depends on who is
 * signed in: an admin sees every user and every session, a practitioner only the patients and
 * sessions of the practice they are working in.
 *
 * Both roles end up in the same store, so the screens keep reading `SubriseUser` and `Session`
 * either way — the difference is only in which endpoint fills it. Every stream here re-runs
 * when the practice is switched, so a switch reloads the screen rather than leaving it empty.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientsDataService {

  private rememberedQuery: UserListQuery | null = null;
  private rememberedSessionQueries = new Map<string, SessionListQuery>();
  /** The last relationships read per client, so a card can show them before its refresh lands. */
  private readonly relationshipsByClient = new Map<string, UserRelationships>();
  private rememberedSessionsListQuery: SessionListQuery | null = null;

  constructor(
    private practiceContextService: PracticeContextService,
    private userService: UserService,
    private userApiService: UserApiService,
    private patientService: PatientService,
    private sessionService: SessionService,
  ) { }

  /** The clients of the current context: every user for an admin, the practice's patients otherwise. */
  get clients$(): Observable<SubriseUser[]> {
    return this.practiceContextService.selectedPractice$.pipe(
      switchMap((practice) => practice ? this.patientService.patients$ : this.userService.users$)
    );
  }

  /**
   * Loads the clients, and reloads them whenever the practice changes. Emits once per load, so
   * a screen clears its loading flag on `next` rather than on completion.
   *
   * This is the only place the client list is fetched — the app bootstrap no longer loads
   * `/users`, so the screens that show clients are what pays for them.
   */
  loadClients(): Observable<SubriseUser[]> {
    return this.practiceContextService.selectedPractice$.pipe(
      switchMap((practice) => practice
        ? this.patientService.load(practice.practiceId, practice.practitionerId)
        : this.userService.load())
    );
  }

  /**
   * Emits once it is known where the clients come from, and again on every practice switch.
   *
   * A paged screen reloads on each emission and fetches nothing before the first one: until the
   * context is resolved there is no telling an admin from a practitioner whose user is still
   * loading, and a page refresh would otherwise ask the wrong endpoint.
   */
  readonly clientsContextChanged$: Observable<void> = this.practiceContextService.resolvedPractice$.pipe(
    map(() => undefined)
  );

  /**
   * One page of the clients of the current context — a single request, so a failure costs the
   * caller that page and nothing else.
   *
   * The endpoint does the paging, searching and ordering, so what comes back is what the screen
   * renders — `clients$` is the whole of what was loaded and cannot answer for a single page.
   */
  loadClientsPage(query: UserListQuery = {}): Observable<PagedResponse<SubriseUser>> {
    return defer(() => {
      const practice = this.practiceContextService.selectedPractice;
      return practice
        ? this.patientService.loadPage(practice.practiceId, practice.practitionerId, query)
        : this.userService.loadPage(query);
    });
  }

  /**
   * The list state the clients overview was last left on.
   *
   * That screen is destroyed on the way to a client and rebuilt on the way back, so what was
   * searched, sorted and paged to would otherwise be gone on every return. Kept here for as long
   * as the app is open; a reload starts from the endpoint's own defaults again.
   */
  get rememberedClientsQuery(): UserListQuery | null {
    return this.rememberedQuery;
  }

  rememberClientsQuery(query: UserListQuery): void {
    this.rememberedQuery = query;
  }

  /**
   * True once a practice scopes the screens. Decides what the clients page offers: an admin
   * creates a client outright, a practitioner invites one into their practice.
   */
  get isPracticeContext$(): Observable<boolean> {
    return this.practiceContextService.resolvedPractice$.pipe(map((practice) => !!practice));
  }

  /** Only the users endpoint filters by role — a practice's patients are all end users. */
  get supportsRoleFilter$(): Observable<boolean> {
    return this.practiceContextService.resolvedPractice$.pipe(map((practice) => !practice));
  }

  /**
   * The list state the sessions overview was last left on — same reason as
   * `rememberedClientsQuery`: the screen is destroyed on the way to a session and rebuilt on the
   * way back. Not the same slot as a client's sessions, which are a list of their own.
   */
  get rememberedSessionsQuery(): SessionListQuery | null {
    return this.rememberedSessionsListQuery;
  }

  rememberSessionsQuery(query: SessionListQuery): void {
    this.rememberedSessionsListQuery = query;
  }

  /**
   * The list state the sessions of a client were last left on, kept per client: a different
   * client is a different list, and inheriting the last one's filter would be a surprise.
   *
   * Same reason as `rememberedClientsQuery` — the panel is destroyed on the way to a session and
   * rebuilt on the way back.
   */
  rememberedClientSessionsQuery(userId: string): SessionListQuery | null {
    return this.rememberedSessionQueries.get(userId) ?? null;
  }

  rememberClientSessionsQuery(userId: string, query: SessionListQuery): void {
    this.rememberedSessionQueries.set(userId, query);
  }

  /**
   * One page of the sessions of the current context: the practice's sessions for a practitioner,
   * every session for an admin.
   */
  loadSessionsPage(query: SessionListQuery = {}): Observable<PagedResponse<Session>> {
    return defer(() => this.sessionService.loadPage({
      ...query,
      affiliationId: this.practiceContextService.affiliationId,
    }));
  }

  /** One page of the sessions of one client. */
  loadClientSessionsPage(userId: string, query: SessionListQuery = {}): Observable<PagedResponse<Session>> {
    return this.loadSessionsPage({ ...query, userId });
  }

  /**
   * What the store already holds for a client, without fetching anything — a client reached from
   * the list is there in summary long before their details are. Lets a screen render the little
   * it knows while `loadClientDetails` fills in the rest.
   */
  clientInStore(userId: string): SubriseUser | null {
    return this.userService.storedUser(userId);
  }

  /**
   * The numeric patient id of a client, once `loadClientDetails` has run for them: that load
   * reads the practice's patient list first, which is where the id is learned. Undefined for an
   * admin, who reaches clients as plain users and has no patient relationship with them.
   */
  patientIdFor(userId: string): number | undefined {
    return this.patientService.patientIdFor(userId);
  }

  /**
   * Details of one client, handed straight back when the store already holds them in full — the
   * screens that show a client are navigated in and out of far more often than a client changes.
   *
   * Keyed on `resolvedPractice$`, so nothing is fetched before it is known where the client comes
   * from. A deep link or a page refresh starts with the current user still loading, and asking
   * then would take an admin's endpoint for a practitioner's client and fail. It emits again on
   * every practice switch, which reloads the screen rather than leaving the previous practice's
   * client on it.
   *
   * A practitioner reaches a patient through the practice endpoints, which key on the numeric
   * patient id — so a deep link, where that id was never learned from a list, loads the list first.
   *
   * `forceRefresh` fetches again regardless, for a caller that has just changed the client.
   */
  /** The relationships as last read for this client, if they were ever read. */
  clientRelationshipsInCache(userId: string): UserRelationships | null {
    return this.relationshipsByClient.get(userId) ?? null;
  }

  /**
   * How a client is tied into the organisation model. An admin's read, kept per client rather
   * than in the store: a practitioner never sees it, so no practice context applies. The client
   * profile page forces a refresh when it appears; anything else takes the cached read.
   */
  loadClientRelationships(userId: string, forceRefresh: boolean = false): Observable<UserRelationships> {
    const cached = this.relationshipsByClient.get(userId);
    if (cached && !forceRefresh) {
      return of(cached);
    }
    return this.userApiService.getRelationships(userId).pipe(
      tap((relationships) => this.relationshipsByClient.set(userId, relationships))
    );
  }

  loadClientDetails(userId: string, forceRefresh: boolean = false): Observable<SubriseUserDetails> {
    return this.practiceContextService.resolvedPractice$.pipe(
      switchMap((practice) => this.clientDetailsIn(practice, userId, forceRefresh))
    );
  }

  private clientDetailsIn(
    practice: UserAffiliation | null,
    userId: string,
    forceRefresh: boolean
  ): Observable<SubriseUserDetails> {
    const loaded = this.userService.detailedUser(userId);
    if (loaded && !forceRefresh) {
      return of(loaded);
    }

    if (!practice) {
      return this.userService.loadDetails(userId);
    }

    const patientId = this.patientService.patientIdFor(userId);
    if (patientId !== undefined) {
      return this.patientService.loadDetails(practice.practiceId, practice.practitionerId, patientId);
    }

    return this.patientService.load(practice.practiceId, practice.practitionerId).pipe(
      switchMap(() => this.patientService.loadDetailsForUser(practice.practiceId, practice.practitionerId, userId))
    );
  }
}
